import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dispatch, SetStateAction, useContext } from 'react';
import { checkActionSubPermission, toTypeCase } from '../../../../../utils/common';
import MlxActionIcon from '../../../../../components/common/MlxActionIcon/MlxActionIcon';
import MlxInputSwitch from '../../../../../components/common/MlxInputSwitch/MlxInputSwitch';
import MlxToast from '../../../../../components/common/MlxToast/MlxToast';
import { useMutation, useQueryClient, UseQueryResult } from 'react-query';
import { _DatatablePageEvent, _OrgStaff, _ToastMessageProps } from '../../../../../constants/staticTypes';
import { defaultToastData } from '../../../../../helpers/commonHelper';
import logger from '../../../../../utils/logger';
import HttpServiceHelper from '../../../../../helpers/httpServiceHelper';
import MlxBlockUI from '../../../../../components/common/MlxBlockUI/MlxBlockUI';
import MlxConfirmPopup from '../../../../../components/common/MlxConfirmPopup/MlxConfirmPopup';
import { confirmPopup } from 'primereact/confirmpopup';
import { CONFIRM_ADMIN_STAFF_DELETE, CONFIRM_ADMIN_STAFF_STATUS_CHANGE } from '../../../../../constants/strings';
import { useTranslation } from '../../../../../hooks/useTranslation';
import { AxiosResponse } from 'axios';
import {
    _AddOrgStaffFormType,
    deleteOrgStaffDetailByID,
    getOrgStaffDetailByID,
    updateOrgStaffStatus,
} from '../../../../../helpers/orgStaffHelper';
import { AlertVariant, OrgPermissionKeys, OrgSubPermissionKeys } from '../../../../../constants/constants';
import { AuthCtx } from '../../../../../context/AuthCtxProvider';
import MlxToolTip from '../../../../../components/common/MlxToolTip/MlxToolTip';

type Props = {
    dataTableEvent: _DatatablePageEvent;
    setDataTableEvent: Dispatch<SetStateAction<_DatatablePageEvent>>;
    openEditAdminStaffSidebar: (data: _AddOrgStaffFormType) => void;
    adminStaffListData: _OrgStaff[];
    setOrgStaffListData: Dispatch<SetStateAction<_OrgStaff[]>>;
    adminStaffListQuery: UseQueryResult<AxiosResponse<any, any>, unknown>;
    setToastMessage?: (toastMessage: _ToastMessageProps) => void;
};

const languageTexts = {
    CONFIRM_ADMIN_STAFF_DELETE: CONFIRM_ADMIN_STAFF_DELETE,
    CONFIRM_ADMIN_STAFF_STATUS_CHANGE: CONFIRM_ADMIN_STAFF_STATUS_CHANGE,
};

function OrgStaffList({
    dataTableEvent,
    setDataTableEvent,
    openEditAdminStaffSidebar,
    adminStaffListData,
    setOrgStaffListData,
    adminStaffListQuery,
    setToastMessage,
}: Readonly<Props>) {
    const translatedText = useTranslation(languageTexts, 'capitalize');
    const getOrgStaffByIDMutation = useMutation(HttpServiceHelper);
    const queryClient = useQueryClient();
    const { userType, userData } = useContext(AuthCtx);

    const handleOrgStaffStatusChange = (rowData: any, event: any) => {
        updateOrgStaffStatus(
            getOrgStaffByIDMutation,
            { id: rowData?.id, newStatus: !event.target.checked },
            (data) => {
                // Update the isActive field in the state
                setOrgStaffListData((prevData: any) =>
                    prevData.map((staff: any) => (staff.id === rowData.id ? { ...staff, isActive: !staff.isActive } : staff))
                );
            },
            (error) => logger.log('Failed to update the status', error)
        );
    };

    const confirmStatusChange = (event: any, rowData: any) => {
        confirmPopup({
            target: event.target,
            message: translatedText.CONFIRM_ADMIN_STAFF_STATUS_CHANGE,
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => handleOrgStaffStatusChange(rowData, event),
            reject: () => {},
        });
    };

    const statusTemplateWithAction = (rowData: any) => {
        // Disable switch if current user is organizationStaff and row user is organizationAdmin, or if the row is the logged-in user
        const isSelf = rowData.id === userData?.id;
        const isDisabled = (userType === 'organizationStaff' && rowData.userType === 'organizationAdmin') || isSelf;

        const handleStatusClick = (event: any) => {
            if (isDisabled) {
                // Show toast notification
                setToastMessage &&
                    setToastMessage({
                        message: isSelf ? 'You cannot change your own status' : 'You cannot modify an Organization Admin staff',
                        variant: AlertVariant.ERROR,
                        show: true,
                    });
            } else {
                confirmStatusChange(event, rowData);
            }
        };

        return (
            <div className="d-flex flex-row align-items-center justify-content-between" style={{ maxWidth: '110px', minWidth: '110px' }}>
                <span className="me-2">{toTypeCase(rowData.isActive ? 'Active' : 'Inactive', 'capitalize')}</span>
                {/* Wrap in a div that we can click even when the switch is "disabled" */}
                <div
                    style={{ opacity: isDisabled ? 0.5 : 1, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                    onClick={(e) => {
                        if (isDisabled) {
                            e.stopPropagation();
                            handleStatusClick(e);
                        }
                    }}
                >
                    <MlxInputSwitch
                        name="admin-staff-status"
                        checked={rowData.isActive}
                        onClick={!isDisabled ? handleStatusClick : undefined}
                    />
                </div>
            </div>
        );
    };
    const handleOrgStaffEdit = (rowData: any) => {
        getOrgStaffDetailByID(
            getOrgStaffByIDMutation,
            { id: rowData?.id },
            (data) => {
                let adminStaffData = data?.data;
                const adminStaffEditableDataForm: _AddOrgStaffFormType = {
                    id: adminStaffData.id,
                    loginUserName: adminStaffData.loginUserName,
                    password: '',
                    preferredName: adminStaffData.preferredName,
                    firstName: adminStaffData.firstName,
                    lastName: adminStaffData.lastName,
                    email: adminStaffData.email,
                    phoneNumber: adminStaffData.phoneNumber,
                    hireDate: adminStaffData.hireDate,
                    staffPermissionRoles: adminStaffData.staffPermissionRoles[0]?.permissionRoleId,
                    venues: adminStaffData.venues?.map((item: any) => {
                        return {
                            venue: {
                                id: item.venueId,
                                venueName: item.venueName,
                            },
                            permissionGroup: {
                                id: item.staffPermissionRole.permissionRoleId,
                                permissionRoleName: item.staffPermissionRole.permissionRoleName,
                            },
                        };
                    }),
                    photoKey: adminStaffData.photoKey,
                    sendPasswordCreation: true,
                    passwordLessLoginOnly: true,
                    sendPasswordCreationTo: 'email',
                    userType: adminStaffData.userType,
                    signedUrl: adminStaffData.signedUrl,
                };
                openEditAdminStaffSidebar(adminStaffEditableDataForm);
            },
            (error) => logger.log('Failed to fetch user details', error)
        );
    };

    const confirmDelete = (event: any, rowData: any) => {
        confirmPopup({
            target: event.currentTarget,
            message: translatedText.CONFIRM_ADMIN_STAFF_DELETE,
            icon: 'pi pi-info-circle',
            defaultFocus: 'accept',
            accept: () => handleOrgStaffDelete(rowData),
            reject: () => {},
        });
    };

    const handleOrgStaffDelete = (rowData: any) => {
        deleteOrgStaffDetailByID(
            getOrgStaffByIDMutation,
            { id: rowData?.id },
            () => {
                adminStaffListQuery.refetch();
            },
            (error) => logger.log('Failed to delete admin staff', error)
        );
    };

    const actionTemplate = (rowData: any) => {
        // Disable actions if current user is organizationStaff and row user is organizationAdmin
        const isDisabled = userType === 'organizationStaff' && rowData.userType === 'organizationAdmin';

        const handleEditClick = () => {
            if (isDisabled) {
                // Show toast notification
                setToastMessage &&
                    setToastMessage({
                        message: 'You cannot edit an Organization Admin staff',
                        variant: AlertVariant.ERROR,
                        show: true,
                    });
            } else {
                handleOrgStaffEdit(rowData);
            }
        };

        const handleDeleteClick = (event: any) => {
            if (isDisabled) {
                // Show toast notification
                setToastMessage &&
                    setToastMessage({
                        message: 'You cannot delete an Organization Admin staff',
                        variant: AlertVariant.ERROR,
                        show: true,
                    });
            } else {
                confirmDelete(event, rowData);
            }
        };

        return (
            <div className="d-flex flex-row align-items-center justify-content-end" style={{ minWidth: '70px' }}>
                {userType === 'organizationAdmin' ||
                checkActionSubPermission(queryClient, OrgPermissionKeys.MANAGE_STAFF, OrgSubPermissionKeys.EDIT_STAFF) ? (
                    <span className={isDisabled ? 'opacity-50' : ''} onClick={isDisabled ? handleEditClick : undefined}>
                        <MlxActionIcon
                            className="me-3 edit-icon-selection"
                            actionType="EDIT"
                            onClick={!isDisabled ? handleEditClick : undefined}
                        />
                    </span>
                ) : (
                    <></>
                )}
                {userType === 'organizationAdmin' ||
                checkActionSubPermission(queryClient, OrgPermissionKeys.MANAGE_STAFF, OrgSubPermissionKeys.DELETE_STAFF) ? (
                    <span className={isDisabled ? 'opacity-50' : ''} onClick={isDisabled ? handleDeleteClick : undefined}>
                        <MlxActionIcon
                            className="me-3 delete-icon-selection"
                            actionType="DELETE"
                            onClick={!isDisabled ? handleDeleteClick : undefined}
                        />
                    </span>
                ) : (
                    <></>
                )}
            </div>
        );
    };

    return (
        <MlxBlockUI isLoading={getOrgStaffByIDMutation.isLoading || adminStaffListQuery.isRefetching} opacity={0.5}>
            <MlxConfirmPopup />
            <DataTable
                className="custom-p-table "
                value={adminStaffListData}
                paginator
                rows={dataTableEvent.rows}
                totalRecords={dataTableEvent.totalRows}
                first={dataTableEvent.first}
                rowsPerPageOptions={dataTableEvent.rowsPerPageOptions}
                size="small"
                removableSort
                scrollable
                scrollHeight="flex"
                paginatorTemplate={dataTableEvent.paginatorTemplate}
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                dataKey="id"
                lazy
                onPage={(event) => setDataTableEvent({ ...dataTableEvent, ...event, forceUpdate: dataTableEvent.forceUpdate + 1 })}
            >
                <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} header="First Name" field="firstName"></Column>
                <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} header="Last Name" field="lastName"></Column>
                <Column
                    style={{ maxWidth: '12rem', minWidth: '12rem' }}
                    field="email"
                    header="Email"
                    body={(rowData) => (
                        <MlxToolTip target={`.staffEmail-${rowData.id}`}>
                            <div
                                className={`staffEmail-${rowData.id} overflow-ellipsis`}
                                data-pr-tooltip={rowData?.email}
                                data-pr-position="bottom"
                            >
                                {rowData?.email}
                            </div>
                        </MlxToolTip>
                    )}
                ></Column>
                <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} field="phoneNumber" header="Phone"></Column>
                {/* <Column style={{ maxWidth: '12rem', minWidth: '12rem' }} header="Organization" body={(rowData) => rowData.org.name}></Column> */}
                <Column style={{ minWidth: '110px' }} header="Status" body={statusTemplateWithAction}></Column>
                {(userType === 'organizationAdmin' ||
                    checkActionSubPermission(queryClient, OrgPermissionKeys.MANAGE_STAFF, OrgSubPermissionKeys.EDIT_STAFF) ||
                    checkActionSubPermission(queryClient, OrgPermissionKeys.MANAGE_STAFF, OrgSubPermissionKeys.DELETE_STAFF)) && (
                    <Column header="" style={{ minWidth: '70px' }} body={actionTemplate}></Column>
                )}
            </DataTable>
        </MlxBlockUI>
    );
}

export default OrgStaffList;
